/**
 * A deal on the board: title, who it is with, its value, and what happens next.
 *
 * The two warnings Pipedrive is built around are the loudest things on it: a
 * **rotten** deal — in its stage too long — gets a red edge, and an open deal
 * with **nothing planned** says so where the next activity would be.
 */
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { DealDto } from '@forsys/shared';
import { ActivityIcon } from './ActivityIcon';
import { ConvertedValue } from './ConvertedValue';
import { dueState, formatDay, formatMoney } from './format';

export function DealCard({ deal, today }: { deal: DealDto; today: string }) {
  const { t } = useTranslation('crm');
  const next = deal.nextActivity;
  const overdue = next !== null && dueState(next.dueDate, today) === 'overdue';
  const counterpart = deal.organization?.name ?? deal.person?.name;

  return (
    <Paper
      variant="outlined"
      title={deal.rotten ? t('card.rotten') : undefined}
      sx={{
        p: 1.25,
        borderLeftWidth: 3,
        borderLeftColor: deal.rotten ? 'error.main' : 'divider',
      }}
    >
      <Link
        component={RouterLink}
        to={`/crm/deals/${deal.id}`}
        underline="hover"
        color="inherit"
        sx={{ fontWeight: 600, display: 'block', overflowWrap: 'anywhere' }}
      >
        {deal.title}
      </Link>
      {counterpart && (
        <Typography variant="caption" color="text.secondary" component="div" noWrap>
          {counterpart}
        </Typography>
      )}
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline', mt: 0.5, gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {formatMoney(deal.value, deal.currency)}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {deal.owner.name}
        </Typography>
      </Stack>
      <ConvertedValue converted={deal.converted} />
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ alignItems: 'center', mt: 0.75, color: next === null ? 'warning.main' : overdue ? 'error.main' : 'text.secondary' }}
      >
        {next === null ? (
          <>
            <WarningAmberIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption">{t('card.noActivity')}</Typography>
          </>
        ) : (
          <>
            <ActivityIcon type={next.type} sx={{ fontSize: 16 }} />
            <Typography variant="caption" noWrap title={next.subject}>
              {formatDay(next.dueDate)}
              {next.dueTime ? ` ${next.dueTime}` : ''} · {next.subject}
            </Typography>
          </>
        )}
      </Stack>
    </Paper>
  );
}
